import { useState, useEffect, useCallback } from 'react'
import { Aircraft } from '../../types/intellijetnet'
import { AircraftOverride } from '../../types/database'
import { supabase } from '../../lib/supabase'
import { MarketEMPsService } from '../../services/market-emps.service'
import { useAircraftOptions } from '../../hooks/useAircraftOptions'
import { formatRelativeValue } from '../../utils/formatters'
import { getEffectiveAircraftData } from '../../utils/overrides'

interface RelativeValueBreakdownProps {
  aircraft: Aircraft
  override?: AircraftOverride | null
  modelId: number
}



interface BreakdownData {
  abbValue: number | null
  maintenanceAdjustment: number
  optionsTotal: number
  empCostAdjustment: number
  totalRelativeValue: number | null
  expectedAFTT: number
  afttDifference: number
  isOnEMP: boolean
  empName: string | null
  empCostPerHour: number | null
  missingData: string[]
  useManufacturingDate: boolean
  yearUsed: number | null
}

export default function RelativeValueBreakdown({ aircraft, override, modelId }: RelativeValueBreakdownProps) {
  const [breakdown, setBreakdown] = useState<BreakdownData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)


  
  // Get aircraft options
  const { data: optionsMap } = useAircraftOptions([aircraft.aircraft_id])

  const calculateBreakdown = useCallback(async () => {
    try {

      setLoading(true)
      setError(null)

      const effectiveAircraft = getEffectiveAircraftData(aircraft, override)
      const missingData: string[] = []

      // Fetch market settings (including use_manufacturing_date)
      const { data: marketSettings, error: settingsError } = await supabase
        .from('market_settings')
        .select('fleet_avg_aftt, maintenance_cost, use_manufacturing_date')
        .eq('model_id', modelId)
        .maybeSingle()

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError
      }

      // Determine which date to use
      const useManufacturingDate = marketSettings?.use_manufacturing_date ?? false
      const yearUsed = useManufacturingDate ? effectiveAircraft.yearmfr : effectiveAircraft.yeardlv

      // Check required data
      if (!yearUsed) {
        missingData.push(useManufacturingDate ? 'Year Manufactured' : 'Year Delivered')
      }
      if (!effectiveAircraft.aftt) {
        missingData.push('Airframe Total Time')
      }

      if (!marketSettings || marketSettings.fleet_avg_aftt === null || marketSettings.maintenance_cost === null) {
        missingData.push('Market Settings (Fleet Average AFTT and Maintenance Cost)')
      }

      // Fetch ABB data
      let abbValue: number | null = null
      if (yearUsed) {
        const { data: abbData, error: abbError } = await supabase
          .from('abb')
          .select('year_model, year, quarter, value')
          .eq('model_id', modelId)
          .eq('year_model', yearUsed)
          .order('year', { ascending: false })
          .order('quarter', { ascending: false })

        if (abbError) throw abbError

        if (abbData && abbData.length > 0) {
          abbValue = abbData[0].value // Most recent value
        } else {
          missingData.push(`ABB Value for ${yearUsed} model year`)
        }
      }

      // Calculate maintenance adjustment
      const currentYear = new Date().getFullYear()
      let maintenanceAdjustment = 0
      let expectedAFTT = 0
      let afttDifference = 0

      if (marketSettings && yearUsed && effectiveAircraft.aftt) {
        expectedAFTT = marketSettings.fleet_avg_aftt * (currentYear - yearUsed)
        afttDifference = expectedAFTT - effectiveAircraft.aftt
        maintenanceAdjustment = afttDifference * marketSettings.maintenance_cost
      }

      // Calculate options total - get fresh data from optionsMap
      const currentOptions = optionsMap.get(aircraft.aircraft_id) || []
      const optionsTotal = currentOptions.reduce((sum, option) => sum + option.value, 0)

      // Calculate EMP cost adjustment
      let empCostAdjustment = 0
      let isOnEMP = false
      let empName: string | null = null
      let empCostPerHour: number | null = null

      const aircraftEMP = override?.emp
      if (aircraftEMP && aircraftEMP.trim() !== '') {
        isOnEMP = true
        empName = aircraftEMP
      } else {
        // Aircraft is NOT on an EMP, apply EMP cost using the active EMP
        const activeEmp = await MarketEMPsService.getActiveByModelId(modelId)

        if (activeEmp && activeEmp.cost_per_hour !== null && activeEmp.cost_per_hour !== undefined && activeEmp.cost_per_hour > 0 && effectiveAircraft.aftt) {
          empCostPerHour = activeEmp.cost_per_hour
          empCostAdjustment = effectiveAircraft.aftt * activeEmp.cost_per_hour
        }
      }

      // Calculate total relative value
      let totalRelativeValue: number | null = null
      if (abbValue !== null && missingData.length === 0) {
        totalRelativeValue = Math.round(abbValue + maintenanceAdjustment + optionsTotal - empCostAdjustment)
      }

      setBreakdown({
        abbValue,
        maintenanceAdjustment,
        optionsTotal,
        empCostAdjustment,
        totalRelativeValue,
        expectedAFTT,
        afttDifference,
        isOnEMP,
        empName,
        empCostPerHour,
        missingData,
        useManufacturingDate,
        yearUsed
      })

    } catch (err: any) {
      setError(err.message || 'Failed to calculate relative value breakdown')
    } finally {

      setLoading(false)
    }
  }, [aircraft, override, modelId, optionsMap])

  useEffect(() => {
    calculateBreakdown()
  }, [calculateBreakdown])

  if (loading) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Relative Value Breakdown</h3>
        <div className="animate-pulse">
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Relative Value Breakdown</h3>
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    )
  }

  if (!breakdown) {
    return null
  }

  const effectiveAircraft = getEffectiveAircraftData(aircraft, override)

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Relative Value Breakdown</h3>
      
      {breakdown.missingData.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-sm text-yellow-800">
            <strong>Missing Data:</strong>
            <ul className="mt-1 list-disc list-inside">
              {breakdown.missingData.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Relative Value (Total) */}
        <div className="flex justify-between items-center py-3 border-b-2 border-gray-200 bg-gray-50 -mx-6 px-6">
          <div className="font-bold text-gray-900 text-lg">Relative Value</div>
          <div className="text-right">
            <div className="font-bold text-lg text-purple-600">
              {formatRelativeValue(breakdown.totalRelativeValue)}
            </div>
          </div>
        </div>

        {/* Base */}
        <div className="flex justify-between items-center py-2 border-b border-gray-100">
          <div>
            <div className="font-medium text-gray-900">Base</div>
            <div className="text-sm text-gray-500">
              {breakdown.yearUsed ? (
                <>
                  {breakdown.yearUsed} model year
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    {breakdown.useManufacturingDate ? 'YOM' : 'EIS'}
                  </span>
                </>
              ) : (
                breakdown.useManufacturingDate ? 'Year of manufacture not available' : 'Entry into service date not available'
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="font-medium">
              {formatRelativeValue(breakdown.abbValue)}
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="flex justify-between items-start py-2 border-b border-gray-100">
          <div className="flex-1 mr-4">
            <div className="font-medium text-gray-900">Options</div>
            <div className="text-sm text-gray-500 mt-1">
              {(() => {
                const selectedOptions = optionsMap.get(aircraft.aircraft_id) || []
                if (selectedOptions.length === 0) {
                  return 'No options selected'
                }
                return (
                  <ul className="list-disc list-inside space-y-1">
                    {selectedOptions.map((option, index) => (
                      <li key={index} className="flex justify-between">
                        <span>{option.name}</span>
                        <span className="ml-2 text-gray-400">{formatRelativeValue(option.value)}</span>
                      </li>
                    ))}
                  </ul>
                )
              })()}
            </div>
          </div>
          <div className="text-right">
            <div className="font-medium text-green-600">
              +{formatRelativeValue(breakdown.optionsTotal)}
            </div>
          </div>
        </div>

        {/* AFTT */}
        <div className="flex justify-between items-center py-2 border-b border-gray-100">
          <div>
            <div className="font-medium text-gray-900">AFTT</div>
            <div className="text-sm text-gray-500">
              Expected: {breakdown.expectedAFTT.toLocaleString()} hrs,
              Actual: {effectiveAircraft.aftt?.toLocaleString() || 'N/A'} hrs
              <br />
              Difference: {breakdown.afttDifference > 0 ? '+' : ''}{breakdown.afttDifference.toLocaleString()} hrs
            </div>
          </div>
          <div className="text-right">
            <div className={`font-medium ${breakdown.maintenanceAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {breakdown.maintenanceAdjustment >= 0 ? '+' : ''}{formatRelativeValue(Math.abs(breakdown.maintenanceAdjustment))}
            </div>
          </div>
        </div>

        {/* EMP */}
        <div className="flex justify-between items-center py-2 border-b border-gray-100">
          <div>
            <div className="font-medium text-gray-900">EMP</div>
            <div className="text-sm text-gray-500">
              {breakdown.isOnEMP ? (
                <>On EMP: {breakdown.empName}</>
              ) : (
                <>
                  Not on EMP
                  {breakdown.empCostPerHour && (
                    <> - Cost: ${breakdown.empCostPerHour.toLocaleString()}/hr</>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className={`font-medium ${breakdown.empCostAdjustment > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {breakdown.empCostAdjustment > 0 ? '-' : ''}{formatRelativeValue(breakdown.empCostAdjustment)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
